"use client"

import { FormEvent, useCallback, useEffect, useRef, useState } from "react"
import {
  retrieveContactSecurity,
  submitContactSubmission,
  type ContactSecurityResponse,
} from "@lib/data/contact-submissions"
import HCaptchaWidget, {
  type HCaptchaWidgetHandle,
} from "@modules/global-shell/components/hcaptcha-widget"
import styles from "./contact-form.module.css"

type ContactTopic = string | { id: string; label: string }
type FormState = "idle" | "captcha-required" | "submitting" | "success" | "error"

const normalizeTopic = (topic: ContactTopic) =>
  typeof topic === "string"
    ? { value: topic, label: topic }
    : { value: topic.id, label: topic.label }

export default function ContactForm({ topics = [] }: { topics?: ContactTopic[] }) {
  const formRef = useRef<HTMLFormElement>(null)
  const captchaRef = useRef<HCaptchaWidgetHandle>(null)
  const [security, setSecurity] = useState<ContactSecurityResponse | null>(null)
  const [securityFailed, setSecurityFailed] = useState(false)
  const [token, setToken] = useState("")
  const [state, setState] = useState<FormState>("idle")
  const [message, setMessage] = useState("")

  useEffect(() => {
    let cancelled = false

    retrieveContactSecurity()
      .then((nextSecurity) => {
        if (cancelled) return
        setSecurity(nextSecurity)
        if (!nextSecurity.enabled || !nextSecurity.site_key) {
          setSecurityFailed(true)
          setState("error")
          setMessage("İletişim formu şu anda kullanılamıyor.")
        }
      })
      .catch(() => {
        if (cancelled) return
        setSecurityFailed(true)
        setState("error")
        setMessage("İletişim formu şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.")
      })

    return () => {
      cancelled = true
    }
  }, [])

  const onCaptchaToken = useCallback((nextToken: string) => {
    setToken(nextToken)
    setState((current) => current === "captcha-required" ? "idle" : current)
    setMessage("")
  }, [])

  const onCaptchaExpired = useCallback(() => {
    setToken("")
  }, [])

  const onCaptchaError = useCallback(() => {
    setToken("")
    setState("error")
    setMessage("hCaptcha doğrulaması kullanılamıyor. Lütfen tekrar deneyin.")
  }, [])

  const resetCaptcha = () => {
    setToken("")
    captchaRef.current?.reset()
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (state === "submitting" || securityFailed || !security?.enabled || !security.site_key) return

    if (!token) {
      setState("captcha-required")
      setMessage("Lütfen hCaptcha doğrulamasını tamamlayın.")
      return
    }

    const data = new FormData(event.currentTarget)
    setState("submitting")
    setMessage("")

    try {
      await submitContactSubmission({
        fullName: String(data.get("name") ?? ""),
        email: String(data.get("email") ?? ""),
        topicId: String(data.get("topic") ?? ""),
        message: String(data.get("message") ?? ""),
        hcaptchaToken: token,
      })
      formRef.current?.reset()
      setState("success")
      setMessage("Mesajınız alındı. Ekibimiz en kısa sürede sizinle iletişime geçecek.")
    } catch (error) {
      setState("error")
      setMessage(error instanceof Error
        ? error.message
        : "Mesajınız gönderilemedi. Lütfen tekrar deneyin.")
    } finally {
      resetCaptcha()
    }
  }

  const busy = state === "submitting"
  const unavailable = securityFailed || (security !== null && (!security.enabled || !security.site_key))

  return (
    <form ref={formRef} className={styles.form} aria-label="İletişim formu" onSubmit={submit}>
      <div className={styles.fieldGrid}>
        <label className={styles.field}>
          <span>Ad Soyad</span>
          <input name="name" type="text" required minLength={2} maxLength={120} autoComplete="name" placeholder="Adınız ve soyadınız" disabled={busy || unavailable} />
        </label>

        <label className={styles.field}>
          <span>E-posta</span>
          <input name="email" type="email" required maxLength={254} autoComplete="email" placeholder="ornek@eposta.com" disabled={busy || unavailable} />
        </label>
      </div>

      {topics.length > 0 ? (
        <label className={styles.field}>
          <span>Konu</span>
          <select name="topic" defaultValue="" required disabled={busy || unavailable}>
            <option value="" disabled>
              Bir konu seçin
            </option>
            {topics.map((topic) => {
              const normalized = normalizeTopic(topic)
              return (
                <option key={normalized.value} value={normalized.value}>
                  {normalized.label}
                </option>
              )
            })}
          </select>
        </label>
      ) : null}

      <label className={styles.field}>
        <span>Mesaj</span>
        <textarea name="message" rows={6} required minLength={10} maxLength={5000} placeholder="Size nasıl yardımcı olabiliriz?" disabled={busy || unavailable} />
      </label>

      {security?.enabled && security.site_key ? (
        <div className={styles.captchaWrap}>
          <HCaptchaWidget
            ref={captchaRef}
            siteKey={security.site_key}
            onToken={onCaptchaToken}
            onExpired={onCaptchaExpired}
            onError={onCaptchaError}
          />
        </div>
      ) : null}

      <button className={styles.submitButton} type="submit" disabled={busy || unavailable || security === null}>
        {busy ? "Gönderiliyor…" : "Mesaj Gönder"}
      </button>

      {message ? (
        <p
          className={state === "success" ? styles.success : styles.formError}
          role={state === "success" ? "status" : "alert"}
        >
          {message}
        </p>
      ) : null}
    </form>
  )
}
